import React, { useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { Shield, ShieldCheck, LayoutDashboard, ShoppingBag, FileCheck2, FileText, Users, Building2, LogOut, BarChart3, Settings, Lock, Plug, BookOpen, Inbox, UserCircle, FileSpreadsheet, Receipt, ScrollText, Truck, Landmark, Package } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useGovCart } from '../../contexts/GovCartContext';
import { GOV_ROLES } from '../../lib/govApi';
import { portalPath } from '../../lib/portal';

const isUskeAdmin = (role) => role === 'super_admin' || role === 'admin';

// Paths under /portal that are viewable WITHOUT authentication (read-only public access).
const PUBLIC_PORTAL_PATHS = ['/catalog', '/help'];
const isPublicPortalPath = (pathname) => {
  const trimmed = pathname.replace(/^\/portal/, '') || '/';
  return PUBLIC_PORTAL_PATHS.some((p) => trimmed === p || trimmed.startsWith(p + '/'));
};

const GovLayout = () => {
  const { user, loading: authLoading, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const cart = useGovCart();

  useEffect(() => {
    document.title = 'USKE Procurement Portal';
  }, []);

  // Restrict portal pages (except landing, login, and public read-only paths) to gov-eligible roles (or USKE admins).
  useEffect(() => {
    if (authLoading) return;          // wait for AuthContext rehydration so hard-refresh deep links work
    const loginPath = portalPath('/login');
    if (!user) {
      if (isPublicPortalPath(location.pathname)) return;   // anonymous browsing allowed
      if (!location.pathname.startsWith(loginPath)) navigate(loginPath, { replace: true });
      return;
    }
    if (!isUskeAdmin(user.role) && !GOV_ROLES.includes(user.role)) {
      navigate('/', { replace: true });
      return;
    }
    // Forced password change: any logged-in user whose account is flagged must be
    // funnelled to /change-password before they can use any other page.
    const changePwPath = portalPath('/change-password');
    if (user.must_change_password && !location.pathname.startsWith(changePwPath)) {
      navigate(changePwPath + '?forced=1', { replace: true });
    }
  }, [user, authLoading, location.pathname, navigate]);

  if (authLoading) return null;
  // Anonymous visitor on a public path — render layout without user-scoped nav.
  if (!user && !isPublicPortalPath(location.pathname)) return null;

  const role = user?.role;
  const isApprover = role === 'approver' || role === 'agency_admin' || role === 'finance_officer' || isUskeAdmin(role);
  const isUske = isUskeAdmin(role);
  const isUskeStaff = role === 'uske_front_desk' || role === 'uske_finance_officer' || isUske;

  const handleLogout = () => {
    logout();
    navigate(portalPath('/login'), { replace: true });
  };

  const navItem = (to, label, Icon, end = false) => (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isActive ? 'bg-blue-900 text-white' : 'text-blue-100 hover:bg-blue-800 hover:text-white'
        }`
      }
      data-testid={`gov-nav-${to.split('/').pop() || 'home'}`}
    >
      <Icon className="h-4 w-4" /> {label}
    </NavLink>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      {/* Government banner */}
      <div className="bg-blue-950 text-blue-100 text-xs py-1.5 px-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Shield className="h-3.5 w-3.5" />
          <span>Official Washington State DES Contract 09925 Portal · Authorized Users Only</span>
        </div>
        <div className="hidden sm:block">Powered by USKE SUPPLY LLC</div>
      </div>

      {/* Top header */}
      <header className="bg-blue-800 text-white shadow-md">
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <Link to={portalPath('/')} className="flex items-center gap-3" data-testid="gov-portal-logo">
            <div className="h-9 w-9 rounded bg-white text-blue-800 flex items-center justify-center font-bold">DES</div>
            <div className="leading-tight">
              <div className="font-bold text-base sm:text-lg">USKE Procurement Portal</div>
              <div className="text-[10px] sm:text-xs text-blue-200">Procurement &amp; Financial Management Platform · Contract No. 09925</div>
            </div>
          </Link>
          <div className="flex items-center gap-3 text-sm">
            {user ? (
              <>
                <div className="hidden sm:block text-right">
                  <div className="font-medium">{user.name}</div>
                  <div className="text-xs text-blue-200 capitalize">{role.replace(/_/g, ' ')}</div>
                </div>
                <Link to={portalPath('/profile')} className="flex items-center gap-1 bg-blue-900 hover:bg-blue-950 px-3 py-1.5 rounded text-xs font-medium" data-testid="gov-profile-link">
                  <UserCircle className="h-3.5 w-3.5" /> Profile
                </Link>
                <button onClick={handleLogout} className="flex items-center gap-1 bg-blue-900 hover:bg-blue-950 px-3 py-1.5 rounded text-xs font-medium" data-testid="gov-logout-btn">
                  <LogOut className="h-3.5 w-3.5" /> Sign out
                </button>
              </>
            ) : (
              <>
                <span className="hidden sm:block text-xs text-blue-200">Viewing as guest · read-only</span>
                <Link to={portalPath('/login')} className="flex items-center gap-1 bg-amber-400 hover:bg-amber-300 text-blue-900 px-3 py-1.5 rounded text-xs font-bold" data-testid="gov-signin-cta">
                  <UserCircle className="h-3.5 w-3.5" /> Sign in to order
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Sub-nav */}
        <nav className="bg-blue-700 border-t border-blue-600">
          <div className="container mx-auto px-2 py-1 flex gap-1 overflow-x-auto">
            {user && navItem(portalPath('/dashboard'), 'Dashboard', LayoutDashboard, true)}
            {(!user || role === 'purchaser' || role === 'agency_admin' || isUske) && navItem(portalPath('/catalog'), 'Catalog', ShoppingBag)}
            {user && (role === 'purchaser' || role === 'agency_admin' || isUske) && (
              <NavLink to={portalPath('/cart')} className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-blue-900 text-white' : 'text-blue-100 hover:bg-blue-800'}`
              } data-testid="gov-nav-cart">
                <ShoppingBag className="h-4 w-4" /> Cart {cart.count > 0 && <span className="bg-amber-400 text-blue-900 rounded-full text-[10px] px-1.5 py-0">{cart.count}</span>}
              </NavLink>
            )}
            {user && navItem(portalPath('/orders'), 'My Orders', FileText)}
            {user && (role === 'finance_officer' || role === 'agency_admin' || role === 'uske_finance_officer' || isUske) && navItem(portalPath('/invoices'), 'Invoices', Receipt)}
            {user && (role === 'finance_officer' || role === 'agency_admin' || role === 'uske_finance_officer' || isUske) && navItem(portalPath('/statements'), 'Statements', ScrollText)}
            {user && (role === 'finance_officer' || role === 'agency_admin' || role === 'uske_finance_officer' || isUske) && navItem(portalPath('/payment-instructions'), 'Payment Instructions', Landmark)}
            {user && isUskeStaff && navItem(portalPath('/fulfilment'), 'USKE Fulfilment', Truck)}
            {user && (role === 'uske_finance_officer' || isUske) && navItem(portalPath('/financial-reports'), 'Financial Reports', BarChart3)}
            {user && (role === 'finance_officer' || isUske) && navItem(portalPath('/admin/finance'), 'Finance', BarChart3)}
            {user && isApprover && navItem(portalPath('/approvals'), 'Approvals', FileCheck2)}
            {user && (role === 'agency_admin' || isUske) && navItem(portalPath('/agency'), 'My Agency', Building2)}
            {user && (role === 'agency_admin' || isUske) && navItem(portalPath('/governance'), 'Governance', ShieldCheck)}
            {user && (role === 'agency_admin' || isUske) && navItem(portalPath('/users'), 'Users', Users)}
            {user && role === 'super_admin' && navItem(portalPath('/admin/products'), 'Product Editor', Package)}
            {user && role === 'super_admin' && navItem(portalPath('/admin/banking'), 'Banking Settings', Landmark)}
            {user && isUske && navItem(portalPath('/admin/agencies'), 'Agencies', Users)}
            {user && isUske && navItem(portalPath('/admin/access-requests'), 'Requests', Inbox)}
            {user && isUske && navItem(portalPath('/admin/reports'), 'Reports', BarChart3)}
            {user && isUske && navItem(portalPath('/admin/fulfillment'), 'Fulfillment', Settings)}
            {user && isUske && navItem(portalPath('/admin/catalog-import'), 'Catalog', FileSpreadsheet)}
            {user && isUske && navItem(portalPath('/admin/punchout'), 'Punchout', Plug)}
            {navItem(portalPath('/help/agency-registration'), 'Help', BookOpen)}
          </div>
        </nav>
      </header>

      <main className="flex-1 container mx-auto px-4 py-6 w-full">
        <Outlet />
      </main>

      <footer className="bg-slate-900 text-slate-400 text-xs py-4 px-4 text-center">
        USKE SUPPLY LLC · Washington State DES Contract 09925 · {new Date().getFullYear()}
      </footer>
    </div>
  );
};

export default GovLayout;
